/**
 * RFC 4180 as the platform writes it, read a piece at a time (ADR 9073).
 *
 * The platform streams its large files (a run's cells, a plan's lines, an initial-allocation run's lists) as CSV: quoted when a field
 * holds a comma, a quote or a line break, the quote doubled; CRLF; a leading byte-order mark. {@link CsvRowReader} is the ONE reader of
 * it — each piece's whole rows are handed back and the rest is kept for the next one, so a field, a quoted line break or a doubled
 * quote cut between two pieces reads exactly as in one, and nothing but the unfinished row is ever held (2026-10-23: a run of 504,246
 * rows ran the tab out of memory when its text was held whole). A final line without a break is read; a blank line is not a row.
 *
 * It was written three times (in-season run cells, the assortment plan files, initial allocation's lists) until it was promoted here
 * (G11: a shared primitive lives in one place and is imported, not copied). What differs between the three is the error a file that
 * ends inside a quoted cell raises, which each caller names: `new CsvRowReader((message) => new MyFileError(message))`.
 */

/** The error a reader raises where its caller names none. */
export class CsvFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CsvFileError';
  }
}

/** The platform's guard against spreadsheet formulas, taken off a cell: `'=1+1` was written for `=1+1`. The export writes it again. */
export const CSV_FORMULA_GUARD = /^'(?=[=+\-@\t\r])/;

export class CsvRowReader {
  private row: string[] = [];
  private field = '';
  private quoted = false;
  private started = false;
  /** A quote ended the last piece inside a quoted cell: the next piece says whether it was doubled. */
  private quoteAtEnd = false;
  private first = true;

  /** `fail` builds the error raised when the file ends inside a quoted cell (default: a {@link CsvFileError}). */
  constructor(private readonly fail: (message: string) => Error = (message) => new CsvFileError(message)) {}

  /** The rows this piece completes; the rest is kept for the next piece. */
  push(piece: string): string[][] {
    let source = piece;
    if (this.first && source !== '') {
      this.first = false;
      if (source.charCodeAt(0) === 0xfeff) source = source.slice(1);
    }
    const rows: string[][] = [];
    const length = source.length;
    let at = 0;
    if (this.quoteAtEnd && length > 0) {
      this.quoteAtEnd = false;
      if (source[0] === '"') { this.field += '"'; at = 1; } else { this.quoted = false; }
    }
    while (at < length) {
      if (this.quoted) {
        // A run of ordinary characters is taken whole: hundreds of thousands of rows are not read a character at a time.
        const next = source.indexOf('"', at);
        if (next === -1) { this.field += source.slice(at); break; }
        this.field += source.slice(at, next);
        if (next + 1 === length) { this.quoteAtEnd = true; break; }
        if (source[next + 1] === '"') { this.field += '"'; at = next + 2; } else { this.quoted = false; at = next + 1; }
        continue;
      }
      const char = source[at] ?? '';
      if (char === '"') { this.quoted = true; this.started = true; at += 1; continue; }
      if (char === ',') { this.row.push(this.field); this.field = ''; this.started = true; at += 1; continue; }
      if (char === '\r' || char === '\n') {
        // A CR ending one piece and its LF starting the next read as a line and an empty one: no row.
        at += char === '\r' && source[at + 1] === '\n' ? 2 : 1;
        if (this.started || this.field !== '') { this.row.push(this.field); rows.push(this.row); }
        this.row = []; this.field = ''; this.started = false;
        continue;
      }
      let end = at + 1;
      while (end < length) {
        const code = source.charCodeAt(end);
        if (code === 44 || code === 34 || code === 10 || code === 13) break;
        end += 1;
      }
      this.field += source.slice(at, end);
      this.started = true;
      at = end;
    }
    return rows;
  }

  /** The last row, when the file did not end with a line break. */
  end(): string[][] {
    if (this.quoteAtEnd) { this.quoteAtEnd = false; this.quoted = false; }
    if (this.quoted) throw this.fail('a quoted cell is never closed');
    const rows: string[][] = [];
    if (this.started || this.field !== '') { this.row.push(this.field); rows.push(this.row); }
    this.row = []; this.field = ''; this.started = false;
    return rows;
  }
}

/** A whole text read as rows — for a caller that holds one. Pure. */
export function parseCsvText(input: string, fail?: (message: string) => Error): string[][] {
  const reader = new CsvRowReader(fail);
  return [...reader.push(input), ...reader.end()];
}
